import React, { memo, useEffect, useState } from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import classNames from 'classnames'

import { RootState } from '../redux/store'
import { ChargePointImport } from '../../common/redux/api/web-api'
import { useValidateImportMutation, useImportChargepointsMutation } from '../redux/api/chargePointsApi'
import { TooltipIcon } from '../../lib/icons/tooltip-icon'
import { Table, TableBody, TableBodyCell, TableHeader, TableHeaderCell, TableRow } from '../../lib/table'
import BootstrapModalLayout from '../../ui/BootstrapModalLayout'
import { IconButton } from '../../common/components/IconButton'
import useText from '../../common/hooks/useText'

import ImportChargePointLabel from './ImportChargePointLabel'

interface IImportChargePointModal {
    close: () => void
    cancel: () => void
}

export const SAlertDiv = styled.div`
    max-height: 40vh;
    overflow-y: scroll;
`

export const STextArea = styled.textarea`
    width: 100%;
    min-height: 20vh;
    max-height: 60vh;
`

export const ImportChargePointModal = memo(function ImportChargePointModal(
    props: IImportChargePointModal
): React.ReactElement {
    const text = useText()
    const [errorMessage, setErrorMessage] = useState<string>()
    const [importChargePointResults, setImportChargePointResults] = useState<ChargePointImport[]>([])
    const [isValid, setIsValid] = useState<boolean>()
    const [pastedLines, setPastedLines] = useState<string>('')
    const [showInfoMessage, setShowInfoMessage] = useState<boolean>(false)

    const companyId = useSelector((state: RootState) => state.user.selectedCompanyId)

    const [validateImport, { isLoading }] = useValidateImportMutation()
    const [importChargePoints] = useImportChargepointsMutation()

    const doImportChargePoints = async () => {
        try {
            await importChargePoints({ companyId, importData: importChargePointResults }).unwrap()

            props.close()
        } catch (exception) {
            console.error(exception)
        }
    }

    useEffect(() => {
        if (errorMessage) {
            alert(errorMessage)
            setErrorMessage(undefined)
        }
    }, [errorMessage])

    const doValidateImport = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const clipboardText = e.clipboardData.getData('text')

        setPastedLines(clipboardText.trim())

        try {
            const retVal = await validateImport({ companyId, importData: clipboardText }).unwrap()
            setImportChargePointResults(retVal)
            const hasErrors = retVal.some((icpr) => icpr.hasError)
            setIsValid(!hasErrors)
        } catch (e) {
            setImportChargePointResults(e.data)
            setErrorMessage(text('msgImportChargersFailed'))
            setIsValid(false)
        } finally {
            setPastedLines('')
        }
    }

    const getDetails = function (importRow: ChargePointImport) {
        let result = `${text('lblImportDetails')}:\n`

        if (importRow.description !== undefined) {
            result += `${text('lblDescription')}: ${importRow.description}\n`
        }
        if (importRow.partnerDescription !== undefined) {
            result += `${text('lblPartnerInfo')}: ${importRow.partnerDescription}\n`
        }
        if (importRow.hasServiceAgreement !== undefined) {
            result += `${text('lblHasServiceAgreement')}: ${importRow.hasServiceAgreement}\n`
        }
        if (importRow.installationDate !== undefined) {
            result += `${text('lblInstallationDate')}: ${importRow.installationDate}\n`
        }
        if (importRow.productType === 'Aura') {
            for (let i = 0; i < importRow.connectors.length; i++) {
                const connectorId = i + 1
                if (importRow.connectors[i].mode !== undefined) {
                    result += `${text('lblSocketMode', { name: connectorId })}: ${importRow.connectors[i].mode}\n`
                }
                if (importRow.connectors[i].nfcLocked !== undefined) {
                    result += `${text('lblSocketRfidLocked', { name: connectorId })}: ${
                        importRow.connectors[i].nfcLocked
                    }\n`
                }
            }
        } else if (importRow.productType === 'Dawn') {
            result += `${text('lblInstallationOfflinePhase')}: ${importRow.connectors[0].loadBalancerOfflinePhase}\n`
            result += `${text('lblInstallationOfflineCurrent')}: ${
                importRow.connectors[0].loadBalancerOfflineCurrent
            }\n`
        } else if (importRow.productType === 'Luna') {
            result += `${text('lblInstallationOfflinePhase')}: ${importRow.connectors[0].loadBalancerOfflinePhase}\n`
            result += `${text('lblInstallationOfflineCurrent')}: ${
                importRow.connectors[0].loadBalancerOfflineCurrent
            }\n`
        } else if (importRow.connectors && importRow.connectors.length > 1) {
            result += `${text('lblChargerMode')}: ${importRow.connectors[0].mode}\n`
            result += `${text('lblSchukoMode')}: ${importRow.connectors[1].mode}\n`
            result += `${text('lblRFIDLocked')}: ${importRow.connectors[0].nfcLocked}\n`
        }
        return result
    }

    const ModalHeader = () => {
        return (
            <>
                {text('lblChargersImportModal')}

                <IconButton onClick={() => setShowInfoMessage(!showInfoMessage)}>
                    <TooltipIcon size={18} />
                </IconButton>
            </>
        )
    }

    return (
        <BootstrapModalLayout
            title={<ModalHeader />}
            actions={[
                {
                    label: text('btnSave'),
                    callback: doImportChargePoints,
                    disabled: !isValid || isLoading,
                    loading: isLoading,
                },
                {
                    label: text('btnCancel'),
                    callback: props.cancel,
                },
            ]}
            size="lg"
            data-testid="import-charger-modal"
        >
            {showInfoMessage && (
                <SAlertDiv className="alert alert-info">
                    <div dangerouslySetInnerHTML={{ __html: text('msgImportChargersFileTip') }} />
                </SAlertDiv>
            )}
            <div className="form-group">
                <STextArea
                    className="form-control"
                    placeholder={text('msgPasteLinesField')}
                    value={pastedLines}
                    onPaste={doValidateImport}
                />
            </div>
            {importChargePointResults && importChargePointResults.length > 0 && (
                <div className="table-container">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell style={{ width: '20%' }} className="heading5">
                                    {text('lblSerialNumberShort')}
                                </TableHeaderCell>
                                <TableHeaderCell className="heading5">{text('lblName')}</TableHeaderCell>
                                <TableHeaderCell style={{ width: '10%' }} className="heading5">
                                    {text('lblSupplyCurrent')}
                                </TableHeaderCell>
                                <TableHeaderCell className="heading5">{text('lblSocketSettings')}</TableHeaderCell>
                                <TableHeaderCell style={{ width: '8%' }} className="heading5">
                                    {text('lblDetails')}
                                </TableHeaderCell>
                                <TableHeaderCell style={{ width: '8%' }} className="heading5">
                                    {text('lblErrorReason')}
                                </TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {importChargePointResults?.map((cpi: ChargePointImport, index: number) => (
                                <TableRow
                                    className={classNames({
                                        danger: cpi.hasError,
                                        warning: cpi.warningReason && cpi.warningReason.length > 0,
                                    })}
                                    key={index}
                                >
                                    <TableBodyCell>{cpi.id}</TableBodyCell>
                                    <TableBodyCell>{cpi.name}</TableBodyCell>
                                    <TableBodyCell>{cpi.supplyCurrent ? `${cpi.supplyCurrent} A` : null}</TableBodyCell>
                                    <TableBodyCell>
                                        <ImportChargePointLabel chargePoint={cpi} />
                                    </TableBodyCell>
                                    <TableBodyCell>
                                        <span className="fa fa-question" title={getDetails(cpi)} />
                                    </TableBodyCell>
                                    <TableBodyCell>
                                        {(cpi.hasError || (cpi.warningReason && cpi.warningReason.length > 0)) && (
                                            <ul className="list-unstyled">
                                                {cpi.errorReason
                                                    .concat(cpi.hasError ? [] : cpi.warningReason)
                                                    .map((error: string, index: number) => (
                                                        <li key={index}>
                                                            <i title={error} className="fa fa-exclamation" />
                                                        </li>
                                                    ))}
                                            </ul>
                                        )}
                                    </TableBodyCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </BootstrapModalLayout>
    )
})
